import { useHeroData } from '../hooks/useTranslations';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

const heroVideo = "/videos/channel-letter-installation.mp4";
const heroPoster = "/videos/channel-letter-installation-poster.jpg";

const Hero = () => {
    const heroData = useHeroData();
    const { isDark } = useTheme();
    const navigate = useNavigate();

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    return (
        <section
            id="hero"
            className="min-h-screen pt-24 pb-10 flex flex-col items-center justify-center text-center relative overflow-hidden m-0"
        >
            {/* Hero Background with Overlay */}
            <div className="absolute inset-0">
                <video
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster={heroPoster}
                    aria-label="Commercial sign installation for a local business"
                >
                    <source src={heroVideo} type="video/mp4" />
                </video>
                <div className={`absolute inset-0 ${isDark
                    ? 'bg-gradient-to-br from-black/80 via-gray-900/75 to-black/65'
                    : 'bg-gradient-to-br from-black/75 via-black/65 to-saratoga/60'
                    }`}></div>
            </div>

            {/* Floating Elements */}
            <div className="hidden md:block absolute top-20 left-10 w-24 h-24 bg-gradient-to-r from-atlantis/30 to-green-yellow-neon/30 rounded-full animate-float blur-sm"></div>
            <div className="hidden md:block absolute top-40 right-20 w-16 h-16 bg-gradient-to-r from-green-yellow-primary/40 to-atlantis/40 rounded-full animate-bounce-gentle blur-sm"></div>
            <div className="hidden md:block absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-r from-green-yellow-neon/20 to-atlantis/20 rounded-full animate-pulse-slow blur-sm"></div>
            <div className="hidden md:block absolute bottom-20 right-1/3 w-12 h-12 bg-gradient-to-r from-atlantis/30 to-green-yellow-primary/30 rounded-full animate-float delay-1000 blur-sm"></div>

            {/* Frase motivacional - Superior Izquierda - Diseño Profesional */}
            <div className="absolute inset-x-0 top-28 md:top-36 w-full max-w-7xl mx-auto px-6 md:px-12 z-20">
                <div className="animate-slide-up delay-300">
                    <div className="text-center md:text-left">
                        {/* Parte principal grande */}
                        <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-3">
                            <span className="text-white drop-shadow-2xl">
                                {heroData.inspirationalMessage || "Set your business apart from the competition."}
                            </span>
                        </h1>
                        
                        {/* Parte descriptiva pequeña - sin fondo */}
                        <div>
                            <p className="text-base md:text-[19px] lg:text-[23px] font-medium text-white/95 leading-relaxed drop-shadow-lg max-w-4xl">
                                {heroData.inspirationalSubtext || "Your brand deserves a visual identity that's more than just a sign. We transform it into a vibrant experience."}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenido inferior - Botones y scroll */}
            <div className="absolute inset-x-0 bottom-6 md:bottom-10 z-10 w-full">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Botones de acción */}
                    <div className="animate-slide-up delay-400 flex flex-col space-y-3 md:flex-row md:space-y-0 md:space-x-6 md:justify-center items-center">
                        <button
                            onClick={() => scrollToSection('contact')}
                            className="w-full md:w-auto group relative inline-flex items-center justify-center px-10 py-5 text-lg md:text-xl font-bold text-black transition-all duration-300 bg-gradient-to-r from-atlantis to-green-yellow-primary rounded-full shadow-2xl hover:shadow-green-yellow-neon/50 hover:scale-105 transform-gpu"
                        >
                            <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-green-yellow-neon to-atlantis rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                            <span className="relative flex items-center">
                                {heroData.discoverMore}
                                <svg className="w-5 h-5 md:w-6 md:h-6 ml-2 md:ml-3 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </span>
                        </button>

                        <button
                            onClick={() => navigate('/portfolio')}
                            className="w-full md:w-auto group relative inline-flex items-center justify-center px-10 py-5 text-lg md:text-xl font-bold transition-all duration-300 rounded-full border-3 border-white/40 backdrop-blur-sm hover:bg-white/20 hover:scale-105 transform-gpu"
                        >
                            <span className={`${isDark ? 'text-white' : 'text-white'} group-hover:text-green-yellow-neon transition-colors flex items-center`}>
                                {heroData.viewServices}
                                <svg className="w-5 h-5 md:w-6 md:h-6 ml-2 md:ml-3 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </span>
                        </button>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default Hero;
